import styled, { css } from "styled-components";

import { breatheBar, breatheChevron, KEEPO_SYMBOL_BAR, KEEPO_SYMBOL_CHEVRON } from "../KeepoLoader";

type Props = {
  disabled?: boolean;
  block?: boolean;
  size: "LG" | "MD" | "SM";
  type: "DANGER" | "INFO" | "WHITE" | "NEUTRAL" | "OUTLINE" | "LEMON";
  uppercase?: boolean;
  nonClickable?: boolean;
  /** Working on it (e.g. saving): the disabled look, with the Keepo symbol breathing in place of the label. */
  $loading?: boolean;
};

// One shape of the Keepo symbol as a mask, so the loading symbol takes the button's colors
const symbolMask = (path: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path d="${path}"/></svg>`
  )}")`;
// The arc turning around the symbol (as KeepoLoader's), drawn in the button's background: an animated SVG
// (it turns and stretches by itself); the still one for whoever asks for less motion
export const loadingArc = (animated: boolean, color = "#767b8f") =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28"><g>${
      animated
        ? '<animateTransform attributeName="transform" type="rotate" from="0 14 14" to="360 14 14" dur="1.6s" repeatCount="indefinite"/>'
        : ""
    }<circle cx="14" cy="14" r="13" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" pathLength="100" stroke-dasharray="${
      animated ? "1 100" : "30 100"
    }">${
      animated
        ? '<animate attributeName="stroke-dasharray" values="1 100;65 100;65 100" keyTimes="0;0.5;1" dur="1.4s" calcMode="spline" keySplines=".42 0 .58 1;.42 0 .58 1" repeatCount="indefinite"/>' +
          '<animate attributeName="stroke-dashoffset" values="0;-15;-99" keyTimes="0;0.5;1" dur="1.4s" calcMode="spline" keySplines=".42 0 .58 1;.42 0 .58 1" repeatCount="indefinite"/>'
        : ""
    }</circle></g></svg>`
  )}")`;

const sizesCss = {
  LG: css`
    padding: 1rem 1.5rem;
    font-weight: bold;
    font-size: 0.875rem;
    line-height: 1rem;
  `,
  MD: css`
    padding: 0.5rem 1rem;
    font-weight: bold;
    font-size: 0.875rem;
    line-height: 1rem;
  `,
  SM: css`
    padding: 0.5rem 1rem;
    font-weight: bold;
    font-size: 0.625rem;
    line-height: 1rem;
  `,
};

const typesCss = {
  DANGER: css`
    background-color: var(--color-feedback-danger);
  `,
  INFO: css`
    background-color: var(--color-brand-blue);
  `,
  WHITE: css`
    background-color: var(--color-neutral-white);
    color: var(--color-neutral-darkness);
  `,
  NEUTRAL: css`
    background-color: var(--color-neutral-base);
  `,
  OUTLINE: css`
    background-color: transparent;
    border: 2px solid var(--color-neutral-light);
  `,
  LEMON: css`
    background-color: var(--color-lemon);
    color: var(--color-brand-blue);
  `,
};

/**
 * Working on it (e.g. saving): the disabled look, with the Keepo symbol breathing in place of the label and the
 * arc turning around it. Shared with the smaller buttons (e.g. the domain screens' SmallButton).
 */
export const buttonLoadingCss = css`
  position: relative;
  color: transparent;
  background-color: var(--color-neutral-dark);
  cursor: progress;
  user-select: none; /* the hidden label can't show up as a selection */
  pointer-events: none;
  background-image: ${loadingArc(true)};
  background-repeat: no-repeat;
  background-position: center;
  background-size: 28px 28px;

  /* The Keepo symbol breathing (as KeepoLoader): the bar and the "‹", one shape each */
  &::before,
  &::after {
    position: absolute;
    inset: 0;
    width: 13px;
    height: 13px;
    margin: auto;
    content: "";
    background: var(--color-neutral-white);
    mask: no-repeat center / contain;
  }

  &::before {
    transform-origin: 24% 50%;
    mask-image: ${symbolMask(KEEPO_SYMBOL_BAR)};
    animation: ${breatheBar} 1s ease-in-out infinite;
  }

  &::after {
    mask-image: ${symbolMask(KEEPO_SYMBOL_CHEVRON)};
    animation: ${breatheChevron} 1s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    background-image: ${loadingArc(false)};

    &::before,
    &::after {
      animation: none;
    }
  }
`;

export const Button = styled.div.attrs<Props>(props => ({ "aria-busy": props.$loading || undefined }))<Props>`
  cursor: ${props => !props?.nonClickable && "pointer"};
  cursor: ${props => props?.disabled && "not-allowed"};

  color: ${props => (props?.disabled ? "var(--color-neutral-base)" : "var(--color-neutral-white)")};

  ${props => typesCss[props?.type]};
  ${props => sizesCss[props?.size]};

  background-color: ${props => props?.disabled && "var(--color-neutral-dark)"};

  border-radius: 6.25rem;
  font-family: "Inter", sans-serif;

  text-transform: ${props => (props?.uppercase ? "uppercase" : "none")};

  display: ${props => (props?.block ? "flex" : "inline-block")};
  align-items: center;
  justify-content: center;
  display: flex;

  width: ${props => props?.block && "100%"};

  ${props => props.$loading && buttonLoadingCss}
`;

export const LinkButton = styled.h6`
  cursor: pointer;
  color: var(--color-neutral-white);
  display: flex;
  justify-content: center;
`;
